import { ISO_3166_1_LOCALE } from '@cineamo/ui/src/helpers/locale-helper';

import { cineamoApiClient } from '../../apiClient';
import { RequestConfig } from '../../fetch-helper';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { MovieDto } from '../../models/movie/v2/MovieDto.types';
import { MovieLocalizedListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

// <------------------------ V2 Movies List ------------------------>

type GetMoviesV2QueryParameter = UrlQueryParameter & {
    releaseDateStart?: string;
    releaseDateEnd?: string;
    region?: string;
    distributorId?: string;
    isShowingNow?: boolean;
    cinemaCountryCode?: ISO_3166_1_LOCALE;
    include?: string;
    /**
     * Movies the cinema has (`true`) or has not (`false`) got a showing scheduled for from now on.
     * Answered for the cinema in `X-Cinema-Id`; sending it without that header is a 400.
     */
    hasScheduledShowings?: boolean;
    /**
     * Movies the cinema has (`true`) or has not (`false`) customized. Answered for the cinema in
     * `X-Cinema-Id`; sending it without that header is a 400.
     */
    hasCustomization?: boolean;
    /** Movies that carry (`true`) or lack (`false`) a Rentrak title. */
    hasRentrakTitle?: boolean;
    /**
     * Movies derived from another film (`true`) or root films only (`false`).
     *
     * A parent is carried by a clone (CIN-14766) and by a re-release with its own Rentrak Title
     * (CIN-15096), and by nothing else, so `true` is the set of movies that are ours rather than
     * TMDB's. Pairs with `parentId` on the returned rows.
     */
    hasParent?: boolean;
    /**
     * Keep only movies with at least this many Cineamo user ratings. Applies on any sort. Pair it
     * with `sort=rating`, where a movie with a single 10/10 otherwise outranks a well-rated one
     * with many. Must be 1 or more — omit it to apply no filter.
     */
    ratingsCountMin?: number;
    /**
     * Director names, OR'd — `{ directors: ['Christopher Nolan'] }`.
     *
     * Matched against the movie's TMDB crew entries where job = 'Director', via a stored generated
     * column with a GIN index. At most 5 names are considered. This is the one relation filter the
     * catalogue can answer, which makes it the route from a movie page into the rest of the
     * catalogue: genre and collection live only inside the tmdbData JSON with no index.
     */
    directors?: string[];
    /**
     * Exact release year — `{ releaseYear: 1994 }` returns only 1994.
     *
     * Distinct from the fuzzy search `year`, which widens to +/-1 because a film's release year
     * differs by region. That is right for a search box and wrong anywhere the year is the subject.
     */
    releaseYear?: number;
};

type MoviesV2Config = {
    locale?: string;
    cinemaId?: number | string;
} & RequestConfig;

/**
 * Builds the V2-specific headers (Accept-Language, X-Cinema-Id).
 */
function buildV2Headers(config?: MoviesV2Config): Record<string, string> {
    const headers: Record<string, string> = {};
    if (config?.locale) {
        headers['Accept-Language'] = config.locale;
    }
    if (config?.cinemaId) {
        headers['X-Cinema-Id'] = config.cinemaId.toString();
    }
    return headers;
}

/**
 * GET /v2/movies
 *
 * Returns a locale-aware paginated list of movies.
 * Locale is resolved via Accept-Language header.
 * Cinema context (customization overrides) via X-Cinema-Id header.
 *
 * @example
 * getMovies({ page: 1, perPage: 10 }, { locale: 'de', cinemaId: 123 })
 */
export async function getMovies(
    urlParams?: GetMoviesV2QueryParameter,
    config?: MoviesV2Config
): Promise<ApiResponse<MovieLocalizedListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<MovieLocalizedListResponse>>(`/v2/movies${buildUrlQuery(urlParams)}`, {
            ...config,
            headers: {
                ...buildV2Headers(config),
                ...config?.headers
            }
        })
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ V2 Movie By ID ------------------------>

/**
 * GET /v2/movies/{movieId}
 *
 * Returns a single locale-aware movie by numeric ID.
 * Supports sparse includes via ?include= query parameter.
 *
 * @example
 * getMovieById(4149063, {
 *   locale: 'de',
 *   cinemaId: 123,
 *   include: 'videos,credits,releaseDates,productionCompanies,productionCountries'
 * })
 */
export async function getMovieById(
    // Accepts undefined so callers can pass a not-yet-resolved id; the guard below returns null.
    movieId: MovieDto['id'] | undefined,
    config?: MoviesV2Config & { include?: string }
): Promise<ApiResponse<MovieDto> | null> {
    if (!movieId) {
        return null;
    }
    const query = config?.include ? `?include=${config.include}` : '';
    return cineamoApiClient
        .get<ApiResponse<MovieDto>>(`/v2/movies/${movieId}${query}`, {
            ...config,
            headers: {
                ...buildV2Headers(config),
                ...config?.headers
            },
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ V2 Movie By Slug ------------------------>

/**
 * GET /v2/movies/{movieSlug}
 *
 * Returns a single locale-aware movie by its slug.
 *
 * @example
 * getMovieBySlug('the-dark-knight', { locale: 'de' })
 */
export async function getMovieBySlug(
    movieSlug: string,
    config?: MoviesV2Config & { include?: string }
): Promise<ApiResponse<MovieDto> | null> {
    if (!movieSlug) {
        return null;
    }
    const query = config?.include ? `?include=${config.include}` : '';
    return cineamoApiClient
        .get<ApiResponse<MovieDto>>(`/v2/movies/${movieSlug}${query}`, {
            ...config,
            headers: {
                ...buildV2Headers(config),
                ...config?.headers
            },
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => null);
}
